import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import moment from "moment";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { getMemberTrackersApi } from "src/DAL/tracker/tracker";
import {
  clearLegacyJourneyDemoStorage,
  daysSince,
  getStepDate,
  groupMemberTrackers,
  isOpenPastDue,
  isRemovedByStaff,
  PREVIEW_LIMIT,
  PRIMARY,
  relativeDayLabel,
  TRACKER_LIST_SECTIONS,
} from "./memberTrackersStorage";
import { JourneyLockChip } from "./JourneyPasswordGate";
import {
  JourneyStatusBadge,
  journeyPrimarySx,
  journeyTabsLookSx,
} from "./MemberJourneyParts";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrCard from "src/design-system/components/GrCard";
import GrIconTile from "src/design-system/components/GrIconTile";
import GrText from "src/design-system/components/GrText";
import GrButton from "src/design-system/components/GrButton";
import GrTabs from "src/design-system/components/GrTabs";
import GrSearch from "src/design-system/components/GrSearch";
import { GrProgressBar } from "src/design-system/components/GrProgress";

// One hairline under the tabs and the search together (phones: under the tabs, search below).
const tabsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  gap: gr.space[4],
  mb: gr.space[6],
  borderBottom: { sm: `1px solid ${gr.color.line}` },
};
const listTabsSx = {
  flex: { xs: "1 1 100%", sm: "1 1 auto" },
  minWidth: 0,
  borderBottom: { xs: `1px solid ${gr.color.line}`, sm: 0 },
  ...journeyTabsLookSx,
};
const searchSx = { width: { xs: "100%", sm: 300 }, mb: { sm: gr.space[2] } };
const sectionTitleSx = {
  fontSize: gr.font.size.xxl,
  fontWeight: gr.font.weight.semibold,
  lineHeight: gr.font.lineHeight.tight,
};
const sectionBlurbSx = { mt: gr.space[2], fontSize: gr.font.size.md, color: gr.color.muted };

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const SECTION_META = {
  [TRACKER_LIST_SECTIONS.MY]: {
    title: "My journeys",
    blurb: "Journeys you’re working on or have already completed.",
  },
  [TRACKER_LIST_SECTIONS.NEW]: {
    title: "New to you",
    blurb: "Journeys you can join — not started yet.",
  },
  [TRACKER_LIST_SECTIONS.LEFT]: {
    title: "Past journeys",
    blurb: "Journeys you've left. You can view your history or rejoin.",
  },
};

const VALID_SECTIONS = Object.keys(SECTION_META);

const strong = (text, color = gr.color.ink) => (
  <Box component="span" sx={{ color, fontWeight: gr.font.weight.semibold }}>
    {text}
  </Box>
);

/** One line under the progress bar: what's due / overdue / next. */
const JourneyNextLine = ({ summary }) => {
  const { open, lastOverdue, overdue, next, status } = summary;
  // Open today: still on time.
  if (open && summary.openToday) {
    return (
      <>
        {strong(open.number ? `Check-in ${open.number}` : "Your check-in", gr.color.brandDark)}{" "}
        is open today
        {overdue.length ? ` · ${overdue.length} missed` : ""}
      </>
    );
  }
  // Its date has passed (open or window closed): missed, can still be sent.
  if (lastOverdue) {
    const days = daysSince(getStepDate(lastOverdue));
    return (
      <>
        Was due{" "}
        {strong(moment(getStepDate(lastOverdue)).format("ddd D MMM"), gr.color.overdue)}
        {days ? ` · ${days} day${days === 1 ? "" : "s"} ago` : ""}
        {overdue.length > 1 ? ` · ${overdue.length} missed` : ""}
      </>
    );
  }
  if (status === "completed") return <>Journey complete — great work!</>;
  if (next && getStepDate(next)) {
    return (
      <>
        Next check-in {strong(moment(getStepDate(next)).format("ddd D MMM"))}
        {" · "}
        {relativeDayLabel(getStepDate(next))}
      </>
    );
  }
  return <>You're all caught up.</>;
};

const cardSx = { display: "flex", flexDirection: "column", gap: gr.space[5] };
const cardTopSx = { display: "flex", gap: gr.space[4], minWidth: 0 };
const pillsSx = { display: "flex", flexWrap: "wrap", gap: gr.space[2], mb: gr.space[2] };
const cardTitleSx = { overflowWrap: "anywhere" };
// My journeys card: the status sits under the name.
const myPillsSx = { ...pillsSx, mt: gr.space[2], mb: 0 };
const progressSx = { gap: gr.space[2] };
const progressValueSx = { fontWeight: gr.font.weight.regular, color: gr.color.muted };
const doneCountSx = { color: gr.color.ink, fontWeight: gr.font.weight.semibold };
const nextLineSx = { flex: 1, color: gr.color.ink2 };
// Inner buttons act on their own: not the card's open-on-click / Enter.
const ownAction = (run) => ({
  onClick: (e) => {
    e.stopPropagation();
    run();
  },
  onKeyDown: (e) => e.stopPropagation(),
});

/** My journeys card: name, status, check-in progress and one action. */
export const MyJourneyCard = ({ tracker, meta, onOpen, onCheckIn }) => {
  const summary = meta.summary;
  // Open today → "Check-in"; its date has passed → "Submit check-in". A password-locked journey
  // has no check-in action: "View journey" opens it (unlock first).
  const action = meta.isPasswordLocked
    ? null
    : summary.open
    ? {
        label: isOpenPastDue(summary.open) ? "Submit check-in" : "Check-in",
        run: () => onCheckIn(null),
      }
    : summary.lastOverdue
      ? { label: "Submit check-in", run: () => onCheckIn(summary.lastOverdue.id) }
      : null;

  return (
    <GrCard as="article" interactive onClick={onOpen} sx={cardSx}>
      <Box sx={cardTopSx}>
        <GrIconTile size="md">
          <GrIcon icon={icons.route} />
        </GrIconTile>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <GrText variant="itemTitle" sx={cardTitleSx}>
            {tracker.name}
          </GrText>
          <Box sx={myPillsSx}>
            <JourneyStatusBadge summary={summary} size="sm" />
            {meta.isPasswordLocked ? <JourneyLockChip /> : null}
          </Box>
        </Box>
      </Box>

      <GrProgressBar
        thick
        value={summary.percent}
        label="Check-ins"
        sx={progressSx}
        valueText={
          <Box component="span" sx={progressValueSx}>
            <Box component="span" sx={doneCountSx}>
              {summary.doneCount}
            </Box>{" "}
            of {summary.total} {summary.ongoing ? "days" : "done"}
            {summary.ongoing ? ` · ${summary.percent}%` : ""}
          </Box>
        }
      />
      <GrText variant="body" sx={nextLineSx}>
        <JourneyNextLine summary={summary} />
      </GrText>

      {/* Journey design: a teal button when something is due, otherwise a plain "View journey". */}
      {action ? (
        <GrButton fullWidth sx={journeyPrimarySx} {...ownAction(action.run)}>
          {action.label}
        </GrButton>
      ) : (
        <GrButton variant="secondary" fullWidth {...ownAction(onOpen)}>
          View journey
        </GrButton>
      )}
    </GrCard>
  );
};

const TrackerCard = ({ tracker, meta, onOpen, onCheckIn, variant = "my" }) =>
  variant === "my" ? (
    <MyJourneyCard
      tracker={tracker}
      meta={meta}
      onOpen={onOpen}
      onCheckIn={onCheckIn}
    />
  ) : (
    <OtherJourneyCard
      tracker={tracker}
      meta={meta}
      onOpen={onOpen}
      variant={variant}
    />
  );

const OtherJourneyCard = ({ tracker, meta, onOpen, variant = "new" }) => (
  <Box
    component="button"
    type="button"
    onClick={onOpen}
    sx={{
      textAlign: "left",
      border: `1px solid ${BORDER}`,
      borderRadius: "14px",
      bgcolor: "#fff",
      p: 2.25,
      cursor: "pointer",
      transition: "border-color 0.15s, box-shadow 0.15s",
      "&:hover": {
        borderColor: PRIMARY,
        boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        mb: 1.25,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "11px",
          bgcolor:
            "color-mix(in srgb, var(--portal-theme-primary) 12%, white)",
          color: PRIMARY,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {meta.isJourney ? (
          <RouteOutlinedIcon fontSize="small" />
        ) : (
          <FlagOutlinedIcon fontSize="small" />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: 0.75,
        }}
      >
        {meta.isPasswordLocked ? <JourneyLockChip /> : null}
        <Chip
          size="small"
          label={
            variant === "left"
              ? tracker.statusName || meta.statusLabel || "Left"
              : variant === "new"
                ? "New"
                : tracker.statusName || "Active"
          }
          sx={{
            height: 24,
            fontWeight: 700,
            fontSize: 11.5,
            bgcolor: `${tracker.statusColor || PRIMARY}18`,
            color: tracker.statusColor || PRIMARY,
          }}
        />
      </Box>
    </Box>

    <Typography
      sx={{
        fontWeight: 800,
        fontSize: 16.5,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.3,
        mb: 0.5,
      }}
    >
      {tracker.name}
    </Typography>
    <Typography
      sx={{
        fontSize: 13.5,
        color: MUTED,
        lineHeight: 1.5,
        mb: 1.75,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {/* "What visitors see" — description is the admin's internal note. */}
      {tracker.visitorMessage}
    </Typography>

    {variant !== "new" ? (
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.75,
          }}
        >
          <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>
            Progress
          </Typography>
          <Typography sx={{ fontSize: 12, color: INK, fontWeight: 700 }}>
            {meta.progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={meta.progress}
          sx={{
            height: 7,
            borderRadius: 99,
            bgcolor: "#eef2f6",
            "& .MuiLinearProgress-bar": {
              borderRadius: 99,
              bgcolor: PRIMARY,
            },
          }}
        />
      </Box>
    ) : null}

    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        pt: 0.5,
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      {variant === "my" ? (
        <>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>
            Submissions:{" "}
            <Box component="span" sx={{ color: INK, fontWeight: 650 }}>
              {meta.submissionsCount}
            </Box>
          </Typography>
          {meta.currentStep ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Next:{" "}
              <Box component="span" sx={{ color: PRIMARY, fontWeight: 700 }}>
                {meta.currentStep.name}
              </Box>
            </Typography>
          ) : null}
          {meta.lastSubmittedAt ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Last: {moment(meta.lastSubmittedAt).fromNow()}
            </Typography>
          ) : null}
        </>
      ) : variant === "left" ? (
        <>
          {meta.leftAt ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Left: {moment(meta.leftAt).format("D MMM YYYY")}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: 12.5, color: PRIMARY, fontWeight: 700 }}>
            {isRemovedByStaff(tracker)
              ? "Removed by your coach · view history"
              : "View history or rejoin"}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: PRIMARY, fontWeight: 700 }}>
          Tap to open & start
        </Typography>
      )}
    </Box>
  </Box>
);

const TrackerSection = ({
  sectionKey,
  rows,
  preview = true,
  search,
  onViewAll,
  onOpen,
  onCheckIn,
}) => {
  const meta = SECTION_META[sectionKey];
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter(({ tracker }) => {
    if (!needle) return true;
    // The coach is not shown on the cards, so it is not searched either.
    return `${tracker.name} ${tracker.visitorMessage || ""}`
      .toLowerCase()
      .includes(needle);
  });
  const visible =
    preview && !needle ? filtered.slice(0, PREVIEW_LIMIT) : filtered;
  const remaining = filtered.length - visible.length;
  const variant =
    sectionKey === TRACKER_LIST_SECTIONS.NEW
      ? "new"
      : sectionKey === TRACKER_LIST_SECTIONS.LEFT
        ? "left"
        : "my";

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: gr.space[4],
          flexWrap: "wrap",
        }}
      >
        <Box>
          <GrText as="h2" sx={sectionTitleSx}>
            {meta.title}
          </GrText>
          <GrText variant="secondary" sx={sectionBlurbSx}>
            {meta.blurb}
          </GrText>
        </Box>
        {preview && remaining > 0 ? (
          <Box
            component="button"
            type="button"
            onClick={onViewAll}
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: PRIMARY,
              fontWeight: 800,
              fontSize: 13.5,
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all ({filtered.length})
          </Box>
        ) : null}
      </Box>

      {filtered.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            border: `1px dashed ${BORDER}`,
            borderRadius: "14px",
            bgcolor: "#f8fafc",
          }}
        >
          <Typography sx={{ color: MUTED, fontSize: 14 }}>
            {needle
              ? "No matches in this section."
              : sectionKey === TRACKER_LIST_SECTIONS.MY
                ? "You’re not on any journeys yet."
                : sectionKey === TRACKER_LIST_SECTIONS.LEFT
                  ? "You haven’t left any journeys."
                  : "Nothing new right now — check back soon."}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
              lg: "1fr 1fr 1fr",
            },
            gap: gr.space[6],
          }}
        >
          {visible.map(({ tracker, meta: cardMeta }) => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              meta={cardMeta}
              variant={variant}
              onOpen={() => onOpen(tracker.id)}
              onCheckIn={(stepId) => onCheckIn(tracker.id, stepId)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function MemberTrackersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userInfo } = useContentSetting();
  const [search, setSearch] = useState("");
  const [trackers, setTrackers] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [loadMessage, setLoadMessage] = useState("");

  const sectionParam = String(searchParams.get("section") || "")
    .trim()
    .toLowerCase();
  const activeSection = VALID_SECTIONS.includes(sectionParam)
    ? sectionParam
    : null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackersApi();
        if (cancelled) return;
        if (res?.code === 200 && Array.isArray(res.trackers)) {
          setTrackers(res.trackers);
          setLoadState("ready");
          setLoadMessage("");
        } else {
          setTrackers([]);
          setLoadState("error");
          setLoadMessage(res?.message || "Could not load journeys");
        }
      } catch (_err) {
        if (cancelled) return;
        setTrackers([]);
        setLoadState("error");
        setLoadMessage("Could not load journeys");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userInfo?._id]);

  useEffect(() => {
    clearLegacyJourneyDemoStorage();
  }, []);

  const grouped = useMemo(() => groupMemberTrackers(trackers), [trackers]);

  const openTracker = (id) => navigate(`/trackers/${id}`);
  // stepId: a missed check-in sent late; none = the open one.
  const checkInTracker = (id, stepId) =>
    navigate(
      `/trackers/${id}/submit${stepId ? `?step=${encodeURIComponent(stepId)}` : ""}`
    );
  const openSection = (key) => setSearchParams(key ? { section: key } : {});

  const tabs = [
    { value: "all", label: "All" },
    { value: TRACKER_LIST_SECTIONS.MY, label: "My journeys", count: grouped.my.length },
    { value: TRACKER_LIST_SECTIONS.NEW, label: "New to you", count: grouped.new.length },
    ...(grouped.left.length
      ? [{ value: TRACKER_LIST_SECTIONS.LEFT, label: "Past", count: grouped.left.length }]
      : []),
  ];

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-lg-8 col-sm-12">
          <h2 style={{ marginBottom: 6 }}>Journeys</h2>
          <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.5 }}>
            Journeys you’re on, plus new ones you can join.
          </Typography>
        </div>
      </div>

      <Box sx={tabsRowSx}>
        <GrTabs
          ariaLabel="Journey sections"
          value={activeSection || "all"}
          onChange={(_e, value) => openSection(value === "all" ? null : value)}
          tabs={tabs}
          sx={listTabsSx}
        />
        <GrSearch
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch("")}
          placeholder="Search journeys…"
          label="Search journeys"
          sx={searchSx}
        />
      </Box>

      {loadState === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : loadState === "error" ? (
        <Typography sx={{ color: MUTED, fontSize: 14.5 }}>
          {loadMessage}
        </Typography>
      ) : (
        <>
          {activeSection === TRACKER_LIST_SECTIONS.MY ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.MY}
              rows={grouped.my}
              preview={false}
              search={search}
              onOpen={openTracker}
              onCheckIn={checkInTracker}
            />
          ) : activeSection === TRACKER_LIST_SECTIONS.NEW ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.NEW}
              rows={grouped.new}
              preview={false}
              search={search}
              onOpen={openTracker}
              onCheckIn={checkInTracker}
            />
          ) : activeSection === TRACKER_LIST_SECTIONS.LEFT ? (
            <TrackerSection
              sectionKey={TRACKER_LIST_SECTIONS.LEFT}
              rows={grouped.left}
              preview={false}
              search={search}
              onOpen={openTracker}
              onCheckIn={checkInTracker}
            />
          ) : (
            <>
              {/* Not on any journey yet: no empty "My journeys" box here (its tab still says so). */}
              {grouped.my.length > 0 ? (
                <TrackerSection
                  sectionKey={TRACKER_LIST_SECTIONS.MY}
                  rows={grouped.my}
                  preview
                  search={search}
                  onViewAll={() => openSection(TRACKER_LIST_SECTIONS.MY)}
                  onOpen={openTracker}
                  onCheckIn={checkInTracker}
                />
              ) : null}
              <TrackerSection
                sectionKey={TRACKER_LIST_SECTIONS.NEW}
                rows={grouped.new}
                preview
                search={search}
                onViewAll={() => openSection(TRACKER_LIST_SECTIONS.NEW)}
                onOpen={openTracker}
                onCheckIn={checkInTracker}
              />
              {grouped.left.length > 0 ? (
                <TrackerSection
                  sectionKey={TRACKER_LIST_SECTIONS.LEFT}
                  rows={grouped.left}
                  preview
                  search={search}
                  onViewAll={() => openSection(TRACKER_LIST_SECTIONS.LEFT)}
                  onOpen={openTracker}
                  onCheckIn={checkInTracker}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
